"use client";
import { OffersOffer, PaginationType } from '@/services/dataTypes';
import React, { useEffect, useState } from 'react';
import { filterOfferBanners, getBannerDimensions } from '@/constants/hooks';
import { apiOfferBanners } from '@/apis/offers';
import { faChevronDown, faChevronUp, FontAwesomeIcon } from '@/constants/icons';
import Banner from './Banner';

interface Props {
    bannerResponse: OffersOffer[];
    domain:         string;
    mer_slug:       string;
    slug_type:      string;
    merchantId:     string;
    companyId:      string;
    pagination:     PaginationType;
}

const VerticalOfferBanner = ({
    bannerResponse, domain, mer_slug, slug_type,
    merchantId, companyId, pagination,
}: Props) => {
    const [banners,     setBanners]     = useState<OffersOffer[]>([]);
    const [page,        setPage]        = useState(2);
    const [hasMore,     setHasMore]     = useState(true);
    const [loading,     setLoading]     = useState(false);
    const [isExpanded,  setIsExpanded]  = useState(false);

    useEffect(() => {
        setBanners(filterOfferBanners(bannerResponse, 20, 2000, 20, 2000));
        if (!pagination?.next_page) setHasMore(false);
    }, [bannerResponse, pagination?.next_page]);

    const loadMore = async () => {
        if (loading || !hasMore) return;
        setLoading(true);
        try {
            const res        = await apiOfferBanners(merchantId, companyId, page);
            const newBanners = filterOfferBanners(res.data?.offers || [], 20, 2000, 20, 2000);
            setBanners(prev => [...prev, ...newBanners]);
            if (res?.data?.pagination?.next_page) {
                setPage(p => p + 1);
                setIsExpanded(true);
            } else {
                setHasMore(false);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const showLess = () => {
        setBanners(filterOfferBanners(bannerResponse, 20, 2000, 20, 2000));
        setPage(2);
        setHasMore(true);
        setIsExpanded(false);
    };

    if (!banners?.length) return null;

    return (
        <div className="flex flex-col gap-3">
            {/* Banners */}
            {banners.map((offer_data, i) => {
                const dim = getBannerDimensions(offer_data);
                return (
                    <div key={i} className="w-full rounded-xl overflow-hidden border border-slate-100 shadow-sm
                                            hover:shadow-md transition-shadow duration-300">
                        <Banner
                            data={offer_data}
                            height={dim?.height}
                            width={dim?.width}
                            mer_slug={mer_slug}
                            slug_type={slug_type}
                            domain={domain}
                        />
                    </div>
                );
            })}

            {/* Load more */}
            {hasMore && (
                <button
                    onClick={loadMore}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                               border border-[#D61C4E]/20 bg-[#D61C4E]/5
                               text-[10px] font-black uppercase tracking-[0.15em]
                               text-[#D61C4E] hover:bg-[#D61C4E] hover:text-white
                               transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loading ? (
                        <div className="flex gap-1">
                            {[0,1,2].map(i => (
                                <div key={i} className="w-1.5 h-1.5 rounded-full bg-current animate-bounce"
                                    style={{ animationDelay: `${i * 0.15}s` }} />
                            ))}
                        </div>
                    ) : (
                        <>
                            Show More
                            <FontAwesomeIcon icon={faChevronDown} className="w-2.5 h-2.5" />
                        </>
                    )}
                </button>
            )}

            {/* Show less */}
            {isExpanded && (
                <button
                    onClick={showLess}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                               border border-slate-100 bg-white text-slate-400
                               text-[10px] font-black uppercase tracking-[0.15em]
                               hover:border-[#D61C4E]/20 hover:text-[#D61C4E]
                               transition-all"
                >
                    Show Less
                    <FontAwesomeIcon icon={faChevronUp} className="w-2.5 h-2.5" />
                </button>
            )}
        </div>
    );
};

export default VerticalOfferBanner;
